import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import { useGlobalShortcuts } from '../hooks/useGlobalShortcuts'
import { Search, X, User, FileText, Package, Building2, DollarSign, Wrench } from 'lucide-react'

/**
 * Global Search Component - Omni-search over alle entiteiten
 */
export function GlobalSearch({ isOpen, onClose, onToggle }) {
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState('all')
  const inputRef = useRef(null)
  const navigate = useNavigate()
  
  // Search query
  const { data: results, isLoading } = useQuery({
    queryKey: ['global-search', query],
    queryFn: async () => {
      if (query.length < 2) return null
      const response = await api.get(`/search?q=${encodeURIComponent(query)}`)
      return response.data
    },
    enabled: query.length >= 2
  })
  
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])
  
  // Centrale shortcut-registry i.p.v. een eigen window-listener.
  // ⌘K/Ctrl+K togglet de zoekbalk (via onToggle); Escape sluit als die open is.
  useGlobalShortcuts([
    { key: 'k', meta: true, preventDefault: true, id: 'global-search-toggle', handler: () => (onToggle || onClose)?.() },
    { key: 'Escape', id: 'global-search-escape', handler: () => { if (isOpen) onClose?.() } },
  ])

  if (!isOpen) return null
  
  const categories = [
    { id: 'all', label: 'Alles', icon: Search, count: results?.total || 0 },
    { id: 'klanten', label: 'Klanten', icon: User, count: results?.klanten?.length || 0 },
    { id: 'projecten', label: 'Projecten', icon: Building2, count: results?.projecten?.length || 0 },
    { id: 'offertes', label: 'Offertes', icon: FileText, count: results?.offertes?.length || 0 },
    { id: 'facturen', label: 'Facturen', icon: DollarSign, count: results?.facturen?.length || 0 },
    { id: 'materialen', label: 'Materialen', icon: Package, count: results?.materialen?.length || 0 },
    { id: 'werkbonnen', label: 'Werkbonnen', icon: Wrench, count: results?.werkbonnen?.length || 0 }
  ]
  
  const getFilteredResults = () => {
    if (!results) return []
    
    if (activeCategory === 'all') {
      return [
        ...(results.klanten || []).map(r => ({ ...r, type: 'klant', icon: User })),
        ...(results.projecten || []).map(r => ({ ...r, type: 'project', icon: Building2 })),
        ...(results.offertes || []).map(r => ({ ...r, type: 'offerte', icon: FileText })),
        ...(results.facturen || []).map(r => ({ ...r, type: 'factuur', icon: DollarSign })),
        ...(results.materialen || []).map(r => ({ ...r, type: 'materiaal', icon: Package })),
        ...(results.werkbonnen || []).map(r => ({ ...r, type: 'werkbon', icon: Wrench }))
      ]
    }
    
    return (results[activeCategory] || []).map(r => ({
      ...r,
      type: activeCategory.slice(0, -1), // Remove trailing 's'
      icon: categories.find(c => c.id === activeCategory)?.icon || Search
    }))
  }
  
  const handleResultClick = (result) => {
    const routes = {
      klant: `/klanten?id=${result.id}`,
      project: `/projecten?id=${result.id}`,
      offerte: `/offertes/${result.id}`,
      factuur: `/facturen?id=${result.id}`,
      materiaal: `/materialen?id=${result.id}`,
      werkbon: `/werkbonnen?id=${result.id}`
    }
    
    navigate(routes[result.type] || '/')
    onClose()
  }
  
  const filteredResults = getFilteredResults()
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[600px] overflow-hidden flex flex-col">
        {/* Search Input */}
        <div className="px-4 py-4 border-b flex items-center gap-3">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek klanten, projecten, offertes, materialen..."
            className="flex-1 outline-none text-lg"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <kbd className="px-2 py-1 text-xs bg-gray-100 rounded border">ESC</kbd>
        </div>
        
        {/* Categories */}
        {query.length >= 2 && (
          <div className="px-4 py-2 border-b bg-gray-50 flex gap-2 overflow-x-auto">
            {categories.map((cat) => {
              const Icon = cat.icon
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1.5 ${
                    activeCategory === cat.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat.label}
                  {cat.count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                      activeCategory === cat.id ? 'bg-blue-700' : 'bg-gray-200'
                    }`}>
                      {cat.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
        
        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {query.length < 2 ? (
            <div className="p-8 text-center text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Type minimaal 2 karakters om te zoeken</p>
              <p className="text-xs text-gray-400 mt-2">
                Gebruik <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Ctrl+F</kbd> om snel te zoeken
              </p>
            </div>
          ) : isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-sm">Zoeken...</p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Geen resultaten gevonden voor "{query}"</p>
              <p className="text-xs text-gray-400 mt-2">
                Probeer andere zoektermen of filters
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredResults.map((result, idx) => {
                const Icon = result.icon
                return (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left transition-colors"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">
                          {result.naam || result.label || result.offerte_nummer || result.nummer || result.id}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium capitalize">
                          {result.type}
                        </span>
                      </div>
                      {result.description && (
                        <div className="text-sm text-gray-500 truncate">
                          {result.description}
                        </div>
                      )}
                      {result.status && (
                        <div className="text-xs text-gray-400 mt-1">
                          Status: {result.status}
                        </div>
                      )}
                    </div>
                    {result.bedrag && (
                      <div className="flex-shrink-0 text-right">
                        <div className="font-semibold text-gray-900">
                          €{result.bedrag.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
          <div>
            {filteredResults.length > 0 && (
              <span>{filteredResults.length} resultaten</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white rounded border text-xs">↑↓</kbd>
              navigeren
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white rounded border text-xs">Enter</kbd>
              selecteren
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white rounded border text-xs">ESC</kbd>
              sluiten
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GlobalSearch
